const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const isVisible = (element) => {
  const style = window.getComputedStyle(element);

  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length)
  );
};

const isShownByOwnStyles = (element) => {
  const style = window.getComputedStyle(element);

  return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
};

document.addEventListener('DOMContentLoaded', () => {
  const dialog = document.getElementById('prospekt-order-dialog');
  const openButtons = document.querySelectorAll('[data-prospekt-modal-open]');
  const orderableCards = document.querySelectorAll('[data-prospekt-card-open]');

  if (!dialog || openButtons.length === 0) {
    return;
  }

  const closeButtons = dialog.querySelectorAll('[data-prospekt-modal-close]');
  const context = dialog.querySelector('#prospekt-order-context');
  const loadingIndicator = dialog.querySelector('[data-prospekt-modal-loading]');
  const formRegion = dialog.querySelector('[data-prospekt-modal-form]');
  const preloadRegion = document.querySelector('[data-prospekt-modal-preload]');
  const gravityFormSelector = [
    '.gform_wrapper form',
    'form[id^="gform_"]',
    '.gform_confirmation_wrapper',
  ].join(',');
  const gravityContainerSelector = '.gform_wrapper, .gform_confirmation_wrapper';
  const gravityLoadingSelector = [
    '.gform_ajax_spinner',
    '.gform-loader',
    '.gform-loader--theme',
    '.gform_spinner',
  ].join(',');
  let opener = null;
  let formObserver = null;
  let isClosing = false;

  const getFocusableElements = () =>
    Array.from(dialog.querySelectorAll(focusableSelector)).filter(isVisible);

  const focusDialogTitle = () => {
    const title = dialog.querySelector('#prospekt-order-title');
    const fallback = dialog.querySelector('[data-prospekt-modal-close]');

    (title || fallback || dialog).focus({ preventScroll: true });
  };

  const setContext = (button) => {
    if (!context) {
      return;
    }

    const title = button.getAttribute('data-prospekt-title');
    context.textContent = title ? `Bestellformular geöffnet für: ${title}.` : '';
  };

  const getActiveFormRegion = () => {
    if (formRegion?.querySelector(gravityFormSelector)) {
      return formRegion;
    }

    if (preloadRegion?.querySelector(gravityFormSelector)) {
      return preloadRegion;
    }

    return formRegion;
  };

  const hasVisibleGravityLoader = () => {
    const activeFormRegion = getActiveFormRegion();

    if (!activeFormRegion) {
      return false;
    }

    return Array.from(activeFormRegion.querySelectorAll(gravityLoadingSelector)).some(isVisible);
  };

  const mountPreloadedForm = () => {
    if (!formRegion || !preloadRegion || formRegion.querySelector(gravityFormSelector)) {
      return;
    }

    while (preloadRegion.firstChild) {
      formRegion.appendChild(preloadRegion.firstChild);
    }

    preloadRegion.setAttribute('hidden', '');
  };

  const isFormReady = () => {
    const activeFormRegion = getActiveFormRegion();

    if (!activeFormRegion) {
      return true;
    }

    const hasForm = !!activeFormRegion.querySelector(gravityFormSelector);
    const hasFormContent = !!activeFormRegion.querySelector(
      'input:not([type="hidden"]), select, textarea, button[type="submit"], .gfield, .gform_confirmation_wrapper'
    );
    const visibleFormContainer = Array.from(
      activeFormRegion.querySelectorAll(gravityContainerSelector)
    ).some(isShownByOwnStyles);

    return hasForm && hasFormContent && visibleFormContainer && !hasVisibleGravityLoader();
  };

  const setFormLoading = (isLoading) => {
    if (loadingIndicator) {
      loadingIndicator.hidden = !isLoading;
    }

    if (formRegion) {
      formRegion.classList.toggle('is-loading', isLoading);
      formRegion.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
  };

  const updateFormLoading = () => {
    const isLoading = !isFormReady();
    setFormLoading(isLoading);

    return !isLoading;
  };

  const observeFormLoading = () => {
    if (!formRegion || formObserver) {
      return;
    }

    formObserver = new MutationObserver(updateFormLoading);
    formObserver.observe(formRegion, {
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'hidden'],
      childList: true,
      subtree: true,
    });
  };

  const refreshFormLoading = () => {
    mountPreloadedForm();

    const isReady = updateFormLoading();

    if (!isReady) {
      observeFormLoading();
      window.setTimeout(updateFormLoading, 120);
      window.setTimeout(updateFormLoading, 600);
      window.setTimeout(updateFormLoading, 1600);
    }
  };

  const openDialog = (button) => {
    if (dialog.open || isClosing) {
      return;
    }

    opener = button;
    setContext(button);
    dialog.classList.remove('is-closing');

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
      dialog.setAttribute('aria-modal', 'true');
    }

    document.documentElement.classList.add('has-prospekt-modal');
    refreshFormLoading();
    window.requestAnimationFrame(focusDialogTitle);
  };

  const closeDialog = () => {
    if (!dialog.open || isClosing) {
      return;
    }

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const canAnimateClose = typeof dialog.close === 'function' && !reduceMotion;
    let closeFinished = false;
    let closeTimer = null;

    const finishClose = () => {
      if (closeFinished) {
        return;
      }

      closeFinished = true;

      if (closeTimer) {
        window.clearTimeout(closeTimer);
      }

      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        dialog.removeAttribute('aria-modal');
      }

      dialog.classList.remove('is-closing');
      document.documentElement.classList.remove('has-prospekt-modal');
      setFormLoading(false);
      isClosing = false;

      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus({ preventScroll: true });
      }
    };

    if (!canAnimateClose) {
      finishClose();
      return;
    }

    isClosing = true;
    dialog.classList.add('is-closing');

    const handleAnimationEnd = (event) => {
      if (event.target === dialog) {
        dialog.removeEventListener('animationend', handleAnimationEnd);
        finishClose();
      }
    };

    dialog.addEventListener('animationend', handleAnimationEnd);
    closeTimer = window.setTimeout(() => {
      dialog.removeEventListener('animationend', handleAnimationEnd);
      finishClose();
    }, 240);
  };

  openButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      openDialog(button);
    });
  });

  orderableCards.forEach((card) => {
    const cardButton = card.querySelector('[data-prospekt-modal-open]');

    if (!cardButton) {
      return;
    }

    card.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : event.target.parentElement;
      const interactiveTarget = target?.closest(
        'a, button, input, select, textarea, label, summary, [role="button"], [role="link"]'
      );

      if (interactiveTarget || event.defaultPrevented) {
        return;
      }

      openDialog(cardButton);
    });
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', closeDialog);
  });

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDialog();
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) {
      closeDialog();
    }
  });

  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = getFocusableElements();

    if (focusableElements.length === 0) {
      event.preventDefault();
      dialog.focus({ preventScroll: true });
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus({ preventScroll: true });
    }
  });

  document.addEventListener('gform/postRender', updateFormLoading);
  document.addEventListener('gform/ajax/post_ajax_submission', updateFormLoading);
  window.addEventListener('load', updateFormLoading);

  if (window.jQuery) {
    window.jQuery(document).on('gform_post_render gform_confirmation_loaded', updateFormLoading);
  }

  setFormLoading(false);
});
